import {
  a11yEn,
  b2bContactEn,
  featuresSectionEn,
  footerExtendedEn,
  organizationsEn,
  pricingTranslationsEn,
  signInEn,
} from './marketingCopy.en'
import { accountAdminCopyEn } from './accountAdminCopy/accountAdminCopy.en'

export const en = {
  hero: {
    title: 'Zporti',
    subtitle: 'Challenge your Friends in the best social sports prediction platform',
    description:
      'Create sports prediction challenges with friends, track your wins, and compete on the leaderboard. The ultimate platform for friendly competition and sports predictions.',
  },
  siteNav: {
    home: 'Home',
    organizations: 'Organizations',
    contact: 'Contact',
    packs: 'Pricing',
    signIn: 'Log in',
    createAccount: 'Sign up',
    openApp: 'Open app',
    signedInAs: 'Signed in as',
    signedInNameFallback: 'Your account',
    signOut: 'Sign out',
    signOutConfirmTitle: 'Sign out?',
    signOutConfirmMessage: 'You will need to sign in again to access your account.',
    signOutConfirm: 'Sign out',
    signOutCancel: 'Cancel',
    signOutErrorTitle: 'Error',
    signOutError: 'Could not sign out. Try again.',
  },
  features: {
    title: 'Why Zporti?',
    subtitle: 'Everything you need to turn sports predictions into friendly competition',
    challenge: {
      title: 'Challenge Friends',
      description:
        'Create custom prediction challenges and invite your friends to compete. Set up group challenges or go head-to-head with individual friends.',
    },
    track: {
      title: 'Track Results',
      description:
        'See real-time scores, track your predictions, and monitor your win rate. Get instant notifications when game results come in.',
    },
    compete: {
      title: 'Compete & Win',
      description:
        'Climb the leaderboard, earn bragging rights, and see who\'s the best predictor. Celebrate victories and track your progress over time.',
    },
    groups: {
      title: 'Groups & seasons',
      description:
        'Keep the same crew together across match weeks, compare standings in your group, and run challenges that feel like a mini league—not a one-off.',
    },
    locks: {
      title: 'Kickoff-aware picks',
      description:
        'Predictions lock when the match starts so everyone plays by the same rules. Outcomes settle from official results—fair, consistent, and dispute-free.',
    },
    organizations: {
      title: 'Organizations & branding',
      description:
        'Clubs, workplaces, and partners can bring their own colors, logos, and member seats—so your community looks like you inside the app.',
    },
    avatars: featuresSectionEn.avatars,
  },
  featuresSection: featuresSectionEn,
  howItWorks: {
    title: 'How It Works',
    subtitle: 'Get started in three simple steps',
    step1: {
      title: 'Create a Challenge',
      description: 'Choose a game, set your predictions, and invite friends to join your challenge.',
    },
    step2: {
      title: 'Make Predictions',
      description:
        'Each participant makes their predictions before the game starts. Lock in your picks and wait for the action.',
    },
    step3: {
      title: 'See Who Wins',
      description:
        'Results are automatically calculated when games end. Celebrate victories and climb the leaderboard.',
    },
  },
  competition: {
    title: 'Friendly Competition, Real Excitement',
    description:
      'Experience the thrill of sports predictions with your friends. Whether you\'re a casual fan or a stats enthusiast, Zporti makes every game more exciting. Challenge your friends, prove your prediction skills, and see who comes out on top.',
  },
  stats: {
    challenges: 'Active Challenges',
    users: 'Active Users',
    predictions: 'Predictions Made',
    games: 'Games Tracked',
  },
  benefits: {
    title: 'Why Choose Zporti?',
    subtitle: 'Everything you need for the ultimate sports prediction experience',
    realTime: {
      title: 'Real-Time Updates',
      description:
        'Get instant notifications when games start, scores update, and results come in. Never miss a moment with live score tracking and automatic result calculations.',
    },
    social: {
      title: 'Social & Connected',
      description:
        'Connect with friends via Gmail, invite them easily, and build your prediction network. Create group challenges or compete one-on-one with your closest friends.',
    },
    smart: {
      title: 'Smart Predictions',
      description:
        'Track odds, analyze match data, and make informed predictions. View historical performance, win rates, and detailed statistics to improve your prediction skills.',
    },
    fair: {
      title: 'Fair & Transparent',
      description:
        'All results are automatically calculated when games end. No disputes, no manual scoring - just fair competition and clear winners.',
    },
  },
  faq: {
    title: 'Frequently Asked Questions',
    subtitle: 'Everything you need to know about Zporti',
    q1: {
      question: 'How do I invite friends?',
      answer:
        'You can invite friends in two ways: connect your Gmail account to easily select contacts, or manually enter email addresses. Friends will receive an invitation link to join your challenge.',
    },
    q2: {
      question: 'When are predictions locked?',
      answer:
        'Predictions must be made before the game starts. Once a match begins, all predictions are locked and cannot be changed. This ensures fair competition for everyone.',
    },
    q3: {
      question: 'How are winners determined?',
      answer:
        'Winners are automatically calculated when games end. The system compares all predictions against actual match results and updates leaderboards in real-time.',
    },
    q4: {
      question: 'Can I create group challenges?',
      answer:
        'Yes! You can create challenges with multiple friends or go head-to-head with individual friends. Set up group competitions to see who has the best prediction skills.',
    },
  },
  packsPage: {
    pageTitle: 'Plans & Pricing',
  },
  contact: {
    title: 'Get in touch',
    subtitle:
      'Product questions, feedback, press, or partnerships—send a message below or email us directly. We read every note.',
    emailLabel: 'Email us at',
    email: 'info@zporti.com',
    form: {
      nameLabel: 'Name',
      emailLabel: 'Email',
      messageLabel: 'Message',
      submitButton: 'Send Message',
      submitting: 'Sending...',
      successMessage: 'Thank you! Your message has been sent. We\'ll get back to you soon.',
      errorMessage: 'Failed to send message. Please try again later.',
      namePlaceholder: 'First and last name',
      emailPlaceholder: 'your.email@example.com',
      messagePlaceholder: 'Tell us what\'s on your mind...',
      validation: {
        nameRequired: 'Name is required',
        nameTooLong: 'Name must be 50 characters or less',
        emailRequired: 'Email is required',
        emailInvalid: 'Please enter a valid email address',
        messageRequired: 'Message is required',
        messageTooLong: 'Message must be 500 characters or less',
        invalidCharacters: 'Invalid characters detected. Please remove any special formatting.',
      },
    },
  },
  footer: {
    title: 'Ready to Start?',
    subtitle:
      'Join Zporti today and start challenging your friends. Create your first prediction challenge in seconds.',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    dataDeletion: 'Data deletion',
    credits: 'Credits',
    privacyControls: 'Privacy controls',
    ...footerExtendedEn,
  },
  pricing: pricingTranslationsEn,
  organizations: organizationsEn,
  b2bContact: b2bContactEn,
  signIn: signInEn,
  a11y: a11yEn,
  accountAdmin: accountAdminCopyEn,
  legal: {
    backToHome: 'Back to home',
    backToPrivacyAndData: 'Back',
    privacyDocumentTitle: 'Privacy Policy',
    dataDeletionDocumentTitle: 'Data deletion instructions',
    termsDocumentTitle: 'Terms of Service',
    creditsDocumentTitle: 'Credits',
    aboutDocumentTitle: 'About Zporti',
    supportDocumentTitle: 'Support',
    documentVersionLine: 'Last updated {date} · version {version}',
    privacyControlsCallout: 'Need to change consent or request your data copy? Open Privacy controls in your account.',
    openPrivacyControls: 'Open Privacy controls',
    signInForPrivacyControls: 'Sign in to open Privacy controls',
  },
  privacyControls: {
    title: 'Privacy controls',
    subtitle: 'Manage marketing consent and request an account data copy.',
    marketingEmailsTitle: 'Marketing emails',
    marketingEmailsHint: 'You can opt in or withdraw any time.',
    analyticsTrackingTitle: 'Analytics tracking',
    analyticsTrackingHint: 'Turn analytics tracking on or off for this device/browser.',
    accountDataCopyTitle: 'Account data copy',
    accountDataCopyHint:
      'Download a JSON copy of personal data linked to your account (available once every 24 hours).',
    enabled: 'Enabled',
    disabled: 'Disabled',
    downloadMyData: 'Download my data',
    nextExportAvailable: 'Next export available',
    close: 'Close',
    deleteYourAccount: 'Delete your account',
    deleteYourAccountHint:
      'Continue to confirm on the next screen. This permanently removes your account and related data.',
  },
}

